import React, { useEffect, useState } from "react";
import type { FieldValues } from "react-hook-form";
import { Routes, Route, Link, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Form,
  Label,
  Input,
  Spinner,
  Alert
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import AnalyticsDashboard from "../../analytics";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_CARD_MIN_HEIGHT_280,
  SUITE_PLACEHOLDER_MIN_200,
  SUITE_DARK_CARD_PREVIEW_120
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function ReportsGalleryPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.analytics.listReports(),
    []
  );
  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.analytics.reports" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          Saved definitions — each tile opens the report runner.
        </p>
      }
    >
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(r => (
            <Col md="6" xl="4" key={r.id} className="mb-4">
              <Card className="h-100 border-0 shadow-sm bg-gradient text-white bg-primary">
                <CardBody>
                  <CardTitle tag="h5">{r.name}</CardTitle>
                  <p className="small opacity-75 mb-2">Owner: {r.owner}</p>
                  <Badge color="light" className="text-primary">
                    {r.schedule}
                  </Badge>
                  {r.lastRunOk === false && (
                    <Badge color="danger" className="ms-1">
                      Last run failed
                    </Badge>
                  )}
                  {r.rowCount != null && (
                    <p className="small opacity-75 mb-0 mt-2">
                      {r.rowCount.toLocaleString()} rows
                    </p>
                  )}
                  <div className="mt-3">
                    <Button
                      tag={Link}
                      color="light"
                      size="sm"
                      to={r.id}
                      relative="path"
                    >
                      Open
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ReportDetailCanvasPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.analytics.getReport(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.analytics.report-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col lg="8">
              {data.lastRunOk === false && (
                <Alert color="danger" className="mb-3">
                  Last scheduled run failed — check warehouse connectivity and
                  row-level permissions.
                </Alert>
              )}
              <Card className="mb-4" style={SUITE_CARD_MIN_HEIGHT_280}>
                <CardBody>
                  <CardTitle>Chart canvas</CardTitle>
                  <div className="bg-light rounded d-flex align-items-center justify-content-center text-muted small" style={SUITE_PLACEHOLDER_MIN_200}>
                    Mount ApexCharts / Recharts here with report id {data.id}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col lg="4">
              <Card>
                <CardBody>
                  <h6>Schedule</h6>
                  <p>{data.schedule}</p>
                  <h6>Owner</h6>
                  <p>{data.owner}</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function BuilderCanvasPage() {
  const { data, loading, reload } = useAsyncResource(
    () => suiteApi.analytics.getBuilderState(),
    []
  );
  const { register, handleSubmit, reset, watch, formState: { isSubmitting } } = useForm();
  const dims = watch("dimensions");
  const metrics = watch("metrics");
  const invalid = data && (!dims?.trim() || !metrics?.trim());

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  async function onSubmit(v: FieldValues) {
    await suiteApi.analytics.saveBuilderState(v);
    reload();
  }

  return (
    <SuitePageShell heading={<IntlMessages id="suite.analytics.builder" />}>
      {loading && !data && <Spinner />}
      {data && (
        <Row>
          <Col md="6" className="mb-4">
            <Card className="border-0 shadow-sm">
              <CardBody>
                <CardTitle tag="h5">Query shape</CardTitle>
                {invalid && (
                  <Alert color="warning" className="py-2 small">
                    Dimensions and metrics are required before you can run this
                    query against the warehouse.
                  </Alert>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="suite.analytics.field-dataset" />
                    </Label>
                    <Input type="text" {...register("dataset")} />
                  </div>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="suite.analytics.field-dimensions" />
                    </Label>
                    <Input type="text" {...register("dimensions")} />
                  </div>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="suite.analytics.field-metrics" />
                    </Label>
                    <Input type="text" {...register("metrics")} />
                  </div>
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    Save draft
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="bg-dark text-white">
              <CardBody>
                <CardTitle tag="h5" className="text-white">
                  Live preview (demo)
                </CardTitle>
                <p className="small text-white-50">
                  Dataset: {data.dataset}
                  <br />
                  BY {data.dimensions}
                  <br />
                  SELECT {data.metrics}
                </p>
                <div
                  className="rounded mt-3"
                  style={SUITE_DARK_CARD_PREVIEW_120}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </SuitePageShell>
  );
}

function ExportsJobsPage() {
  const { data, loading, error, reload } = useAsyncResource(
    () => suiteApi.analytics.listExports(),
    []
  );
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <SuitePageShell heading={<IntlMessages id="suite.analytics.exports" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <div className="list-group shadow-sm">
          {(data || []).map(ex => (
            <div
              key={ex.id}
              className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2"
            >
              <div>
                <strong>{ex.name}</strong>
                <div className="small text-muted">{ex.created}</div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge
                  color={
                    ex.status === "ready"
                      ? "success"
                      : ex.status === "failed"
                        ? "danger"
                        : "warning"
                  }
                >
                  {ex.status}
                </Badge>
                {ex.status === "failed" && (
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    disabled={busy === ex.id}
                    onClick={async () => {
                      setBusy(ex.id);
                      await suiteApi.analytics.retryExport(ex.id);
                      reload();
                      setBusy(null);
                    }}
                  >
                    Retry job
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function AnalyticsSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="analytics" />}>
        <Route index element={<AnalyticsDashboard />} />
        <Route path="reports" element={<ReportsGalleryPage />} />
        <Route path="reports/:id" element={<ReportDetailCanvasPage />} />
        <Route path="builder" element={<BuilderCanvasPage />} />
        <Route path="exports" element={<ExportsJobsPage />} />
      </Route>
    </Routes>
  );
}
